import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Search, X } from 'lucide-react'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { Button, Card, CardBody, Container, Modal } from 'react-bootstrap'
import { formatDateFull } from '../../../../component/solution/utils/AppUtil';
import { IQuerySelectData } from '../../../../component/solution/Interfaces';
import { useAuth } from '../../../../contexts/AuthContext';
import ShowErrorAlert from '../../../../component/ShowErrorAlert';
import { QueryListProvider } from '../../../../component/solution/QueryTableList/QueryListContext';
import { QueryTableList } from '../../../../component/solution/QueryTableList/QueryTableList';

export interface IStockAudits {
  id: string;
  created: string;
  reference: string;
  count_type: string;
  location: string;
  location_name: string;
  categories: string | null;
  open_soh: number;
  scanned_qty: number;
  close_soh: number;
  negative_adjustments: number;
  positive_adjustments: number;
  unique_article_codes: number;
  total_sku: number;
  comments: string | null;
  time_to_complete: {
    type: string;
    value: string;
    years: number;
    months: number;
    days: number;
    hours: number;
    minutes: number;
    wholeSeconds: number;
    microSeconds: number;
    seconds: number;
    null: boolean;
  };
  action: string;
  success: boolean;
}

export interface IStockAuditReportReview {
  reference: string;
  item_code: string;
  stocktake_id: string;
  location: string;
  scanned: boolean;
  name: string;
  on_hand_start: number;
  committed_start: number;
  available_start: number;
  scanned_qty: number;
  scanned_variance: number;
  on_hand_final: number;
  committed_final: number;
  actual_variance: number;
  action: string;
  success: boolean;
}

export const StockCountAudits: React.FC = () => {

    const { fetchData, showErrorAlert, responseErrorMessage  }  = useAuth();

    const queryUrlSelectWithCount: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data?includeCount=true"}`;
   // const queryExportReportUrl: string = `${process.env.REACT_APP_BASE_URL}/select/ares/stocktake-audit/xlsx`;
    const fetchStockTakeActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/STOCKTAKE-ACTIONS/run`; 

    const [selectedStockAudit, setSelectedStockAudit] = useState<IStockAudits | null>(null); 
    const [selectedReportReview, setSelectedReportReview] = useState<IStockAuditReportReview[] | null>(null);

    const [showReportPreviewModal, setShowReportPreviewModal] = useState<boolean>(false);
    const [reportLoading, setReportLoading] = useState<boolean>(false);
    const [downLoadLoading, setDownLoadLoading] = useState<boolean>(false);

  
    //handle report screen view 
    const handleReportView = async (record: IStockAudits) => {
       // 
       if(!record) return; 
       
        setSelectedStockAudit(record);
        setShowReportPreviewModal(true);
        
        const queryStateStockAuditsReport: IQuerySelectData = {
            appCode: "ares",
            selectCode: "stocktake-report",  
            where: 'stocktake_id = :id',
            params: { 'id': `${record.id}`}
        }; 

        setReportLoading(true);
        fetchData(queryUrlSelectWithCount, {method:"POST", body: JSON.stringify(queryStateStockAuditsReport)}, (result: any) => processReportPreviewData(result), cleanUpReportLoader);
   };
 
   // handle the report preview data
   const processReportPreviewData = (result: any) => {

        const reportPreview: IStockAuditReportReview[] = [];

        //result.data is an array or object of entries
        for (const key in result?.data) {
            const record = result.data[key];

            reportPreview.push({
                // merge key and data into one flat object
                ...(record.key || {}),
                ...(record.data || {}),
                action: record.action,
                success: record.success
            });
        }

        setSelectedReportReview(reportPreview);
   };

    //default report up loader
    const cleanUpReportLoader = () => {
        setReportLoading(false);
    }

    // handles the resetting when report preview is closed.
    const handleCloseReportPreview = () => {
        setShowReportPreviewModal(false); 
        setSelectedReportReview(null);
        // setSelectedStockAudit(null);
    }

    //handling reporting down
    const handleReportDownLoad = (stockAudit:IStockAudits) => {
        setDownLoadLoading(true);
        fetchData(`${fetchStockTakeActionsQueryUrl}?stocktake_id=${stockAudit.id}`, {method: "GET" }, (result: any) =>  processStockTakeReportExcel(result, stockAudit.reference), cleanUpLoaderDownLoad);
    }
   
    // handling the process of formating report to excel
    const processStockTakeReportExcel = async (result: any, stockTakeReference: string) => {
       
       // Create a download link for the Blob
       const url = URL.createObjectURL(result);
       // Create a temporary <a> element to trigger the download
       const a = document.createElement('a');
       a.href = url;
       //a.download = `${confirmShipmentNo}.xlsx`; // Set the default filename here
       a.download = `${stockTakeReference}.xlsx`; // Set the default filename here
       document.body.appendChild(a);
       a.click();
    } 

    //default download loader
    const cleanUpLoaderDownLoad = () => {
        setDownLoadLoading(false);
    }


    //Performing Total Calculations
    const totalOnHandStart = selectedReportReview?.reduce((total, report) => total + report.on_hand_start, 0);
    const totalCommittedStart = selectedReportReview?.reduce((total, report) => total + report.committed_start, 0);
    const totalAvailableStart = selectedReportReview?.reduce((total, report) => total + report.available_start, 0);
    const totalScannedQty = selectedReportReview?.reduce((total, report) => total + report.scanned_qty, 0);
    const totalScannedVarience = selectedReportReview?.reduce((total, report) => total + report.scanned_variance , 0);
    const totalOnHandFinal = selectedReportReview?.reduce((total, report) => total + report.on_hand_final , 0);
    const totalCommittedFinal = selectedReportReview?.reduce((total, report) => total + report.committed_final, 0);
    const totalActaulVarience = selectedReportReview?.reduce((total, report) => total + report.actual_variance, 0);

  return (
    <React.Fragment>

          <Container fluid className="p-0">
            <QueryListProvider>
                    <QueryTableList
                        listTitle={"Insights - Stock Count Audits"}
                        recordTitle={"Insights - Stock Count Audits"}
                        tableName={""}
                        editTableName={""}
                        hasIntegrationStatusLegend={false} 
                        errorCategoryName={''}
                        editHasCompoundKey={false}  
                        showFailedTransactionsTable={false}
                        query={{
                              appCode: "ares",
                              selectCode: "stocktake-audit",
                              tableName: null, 
                              where: "",
                              orderBy: "",
                              pageNum: 0,
                              pageSize: 100,
                              params: {},
                        }}   
                        orderColumns={[
                            { name: "reference", type: "STRING", sortable: true, searchable: true },
                            { name: "count_type", type: "STRING", sortable: true, searchable: true },
                            { name: "location", type: "STRING", sortable: true, searchable: true },
                            { name: "location_name", type: "STRING", sortable: true, searchable: true },
                            { name: "categories", type: "STRING", sortable: true, searchable: true },
                            { name: "open_soh", type: "NUMBER", sortable: false, searchable: false },
                            { name: "close_soh", type: "NUMBER", sortable: false, searchable: false },
                            { name: "negative_adjustments", type: "NUMBER", sortable: false, searchable: false },
                            { name: "positive_adjustments", type: "NUMBER", sortable: false, searchable: false },
                            { name: "unique_article_codes", type: "NUMBER", sortable: false, searchable: false },
                            { name: "total_sku", type: "NUMBER", sortable: false, searchable: false },
                            { name: "comments", type: "STRING", sortable: true, searchable: true },
                            { name: "created", type: "DATE", sortable: true, searchable: false },
                            { name: "time_to_complete", type: "STRING", sortable: true, searchable: false }
                        ]}
                        reportOptions={{ 
                            reportQuery: {
                              appCode: "ares",
                              selectCode: "stocktake-audit",
                              tableName: null, 
                              where: "",
                              orderBy: "",
                              pageNum: 0,
                              pageSize: 0,
                              params: {},
                            },
                            templateName: "apps/ares/stocktake_audit_template.xlsx", 
                            reportName: "stocktake_audit_report.xlsx",
                            reportData: null
                        }}      
                        renderRowData={(row) => {
                        
                            const tdStyle: React.CSSProperties = { whiteSpace: 'nowrap', border: '1px solid #deebed',  padding: '4px 8px', fontSize: '0.75rem' };
                            const tdEllipsis: React.CSSProperties = {...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}
                            const tdHasDetailsOption: React.CSSProperties = { ...tdStyle,  textDecoration: 'underline', cursor: 'pointer'};

                            return (
                                <>
                                <td style={tdHasDetailsOption} title={row.reference}>
                                    <span onClick={() => handleReportView(row)}>{row.reference}</span>
                                </td>
                                <td style={tdStyle} title={row.count_type}>{row.count_type}</td>
                                <td style={tdStyle} title={row.location}>{row.location}</td>
                                <td style={tdStyle} title={row.location_name}>{row.location_name}</td>
                                <td style={tdStyle} title={row.categories}>{row.categories}</td>
                                <td style={tdStyle} title={row.open_soh}>{row.open_soh}</td>
                                <td style={tdStyle} title={row.close_soh}>{row.close_soh}</td>
                                <td style={tdStyle} title={row.negative_adjustments}>{row.negative_adjustments}</td>
                                <td style={tdStyle} title={row.positive_adjustments}>{row.positive_adjustments}</td>
                                <td style={tdStyle} title={row.unique_article_codes}>{row.unique_article_codes}</td>
                                <td style={tdStyle} title={row.total_sku}>{row.total_sku}</td>
                                <td style={tdEllipsis} title={row.comments}>{row.comments}</td>
                                <td style={tdStyle} title={row.created}>{formatDateFull(row.created)}</td>
                                <td style={tdStyle} title={row.time_to_complete?.value ?? ""}>{row.time_to_complete?.value ?? ""}</td>
                                </>    
                            );
                        }}

                        renderActions={(row) => {
                        
                            return (
                              <td className="sa-record-control" style={{ border: '1px solid #deebed', height: '45px', fontSize: '0.55rem', padding: '0 5px'}}>
                                
                             </td>
                            );
                        }}   
                    />
                </QueryListProvider>


                {/* Modal Report Preview [Start] */}
                 <>
                 <Modal show={showReportPreviewModal} onHide={handleCloseReportPreview} size="xl" aria-labelledby="example-custom-modal-styling" className="custom-modal-width">
                    <Modal.Header closeButton>
                    <Modal.Title>
                        Stock Count Audits : [ {selectedStockAudit?.reference} ] - Report Preview 
                        <button className="btn btn-secondary" style={{ margin: "0px 0px 0px 21px"}}  disabled={downLoadLoading}
                                onClick={() => { if(selectedStockAudit) handleReportDownLoad(selectedStockAudit);}}
                        >
                            Download Report
                        </button>
                        </Modal.Title>
                    </Modal.Header>
                        <Modal.Body>
                            {downLoadLoading && 
                              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", width: "100%" }}>
                                <div style={{ width: "100%", textAlign: "center" }}>
                                  <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                </div>
                                <div className="report-downloading-message-display" style={{ width: "100%", textAlign: "center" }}>
                                  <span className="download-message" style={{ color: "green" }}>
                                     Processing your report for download...
                                  </span>
                                </div>
                              </div>                              
                            }
                            { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 
                            <table className="table table-bordered">
                                <thead>
                                      <tr>
                                          <th></th>
                                          <th><b className='report-th-bolder'>TOTALS:</b></th>
                                          <th className='report-th-not-bold'>{totalOnHandStart}</th>
                                          <th className='report-th-not-bold'>{totalCommittedStart}</th>
                                          <th className='report-th-not-bold'>{totalAvailableStart}</th>
                                          <th className='report-th-not-bold'>{totalScannedQty}</th>
                                          <th className='report-th-not-bold'>{totalScannedVarience}</th>
                                          <th className='report-th-not-bold'>{totalOnHandFinal}</th>
                                          <th className='report-th-not-bold'>{totalCommittedFinal}</th>
                                          <th className='report-th-not-bold'>{totalActaulVarience}</th>
                                      </tr>
                                      <tr>
                                          <th><b className='report-th-bolder'>SKU</b></th>
                                          <th><b className='report-th-bolder'>Name</b></th>
                                          <th><b className='report-th-bolder'>On Hand (Start)</b></th>
                                          <th><b className='report-th-bolder'>Committed (Start)</b></th>
                                          <th><b className='report-th-bolder'>Available (Start)</b></th>
                                          <th><b className='report-th-bolder'>Scanned Qty</b></th>
                                          <th><b className='report-th-bolder'>Scanned Varience</b></th>
                                          <th><b className='report-th-bolder'>On Hand (Final)</b></th>
                                          <th><b className='report-th-bolder'>Committed (Final)</b></th>
                                          <th><b className='report-th-bolder'>Actaul Varience</b></th>
                                      </tr>
                                </thead>
                                <tbody>
                                {reportLoading ? (
                                    <tr>
                                          <td colSpan={10} className="text-center">
                                              <div className="spinner-border" role="status">
                                                  <span className="visually-hidden">Loading...</span>
                                              </div>
                                          </td>
                                      </tr>
                                  ) : (
                                    selectedReportReview && selectedReportReview.map((report:IStockAuditReportReview) => (
                                      <tr key={report.stocktake_id}>
                                         <td style={{ whiteSpace: 'nowrap' }}>{report.item_code}</td>
                                         <td style={{ whiteSpace: 'nowrap' }}>{report.name}</td>
                                         <td>{report.on_hand_start}</td>
                                         <td>{report.committed_start}</td>
                                         <td>{report.available_start}</td>
                                         <td>{report.scanned_qty}</td>
                                         <td>{report.scanned_variance}</td>
                                         <td>{report.on_hand_final}</td>
                                         <td>{report.committed_final}</td>
                                         <td>{report.actual_variance}</td>
                                      </tr>
                                     ))
                                  )}
                                </tbody>
                            </table>         
                        </Modal.Body>
                        <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseReportPreview}>
                            Cancel
                        </Button>
                        </Modal.Footer>
                    </Modal>
                  </> 
                {/* Modal Report Preview [End] */}

            </Container> 
    </React.Fragment>    
  )
}
